import React, { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import cloneDeep from 'lodash/cloneDeep';
import Form from 'react-bootstrap/Form';
import classes from './CollectionSearch.module.scss';
import t from '@/utils/Language/Collection-translations';
import AutoComplete from '@/features/googleMap/components/GoogleLocation/AutoComplete';
import Button from '@/components/Button/Button';
import { viewportContext } from '@/context/viewport.context';
import unset from 'lodash/unset';

const CollectionSearch = ({ onSearch, onSort, recordCount, urlQueryParameters, ...props }) => {
  const autoCompleteElementId = 'collection-search-autocomplete';
  const router = useRouter();
  const { isMobileOrTablet } = useContext(viewportContext);
  // This default of alphabetical is actually not used to determine the default & seems to make no difference
  const [selectedSortBy, setSelectedSortBy] = useState(urlQueryParameters?.sortby || 'alphabetical');
  const [, setFormData] = useState({ search_value: urlQueryParameters?.q || '' });
  const [showSearchCount, setShowSearchCount] = useState(false);
  const [locationSearch, setLocationSearch] = useState({ locationName: '' });
  const [searchKeyword, setSearchKeyword] = useState('');

  const sortByOptions = () => {
    // TODO - update API to handle sort by Number of resources and restore this option
    return [
      { value: 'alphabetical', name: t('Alphabetical'), field: 'title' },
      { value: 'dateupdated', name: t('Date Updated'), field: 'updated_at', order: 'desc' },
      { value: 'datecreated', name: t('Date Created'), field: 'created_at', order: 'desc' }
      // { value: 'resources', name: t('Number of resources'), field: 'resource_count_total', order: 'desc' }
    ];
  };

  useEffect(() => {
    const element = document.getElementById(autoCompleteElementId);
    if (element) {
      element.autocomplete = 'off';
    }
  }, []);

  useEffect(() => {
    if (locationSearch.geometry) {
      updateSearch();
    }
  }, [locationSearch]);

  useEffect(() => {
    setFormData({ search_value: router.query.q });
    setSelectedSortBy(router.query.sortby || selectedSortBy);
    if (router.query.q) {
      setShowSearchCount(true);
    } else {
      setShowSearchCount(false);
    }
  }, [router.query.q, router.query.sortby]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (typeof onSearch === 'function') {
      onSearch({ q: searchKeyword });
    }
    updateSearch();
  };

  const updateSearch = () => {
    router.push({
      pathname: router.pathname,
      hash: window?.location?.hash,
      query: Object.assign({}, router.query, {
        q: searchKeyword,
        lat: locationSearch?.geometry?.location.lat(),
        long: locationSearch?.geometry?.location.lng(),
        sortby: selectedSortBy
      })
    });
  };

  const handleSortChange = (e) => {
    e.preventDefault();
    setSelectedSortBy(e.target?.value);
    if (typeof onSort === 'function') {
      onSort({ sortBy: e.target?.value });
    }
    router.push({
      pathname: router.pathname,
      hash: window.location.hash,
      query: Object.assign({}, router.query, {
        sortby: e.target?.value
      })
    });
  };

  const handleChange = (e) => {
    setSearchKeyword(e.target.value);
  };

  const clearSearch = () => {
    const element = document.getElementById(autoCompleteElementId);
    element.value = '';
    setSearchKeyword('');
    let clearedQuery = cloneDeep(router.query);
    unset(clearedQuery, 'sortby');
    unset(clearedQuery, 'q');
    unset(clearedQuery, 'lat');
    unset(clearedQuery, 'long');
    router.push({
      pathname: router.pathname,
      hash: window.location.hash,
      query: { ...clearedQuery }
    });
  };

  return (
    <div className={classes.collectionSearchContainer}>
      <div className={classes.filterForm}>
        <div className={[classes.formSet, classes.formSortBy].join(' ')}>
          <Form.Label className={classes.formLabel}>{t('Sort by')}</Form.Label>
          <Form.Select data-cy="select-filter" onChange={handleSortChange} value={selectedSortBy} name={'sortBy'} className={classes.inputBox}>
            {sortByOptions().map((option, index) => {
              return (
                <option
                  key={index}
                  name={option.name}
                  value={option.value}
                  data-cy={`select-filter-option-${option.name.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {option.name}
                </option>
              );
            })}
          </Form.Select>
        </div>
        <div className={[classes.formSet, classes.formSearchBy].join(' ')}>
          <Form onSubmit={handleSubmit}>
            <Form.Group controlId="formBasicName">
              <Form.Label className={classes.formLabel}>{t('Search by keyword or author')}</Form.Label>
              <Form.Control
                data-cy="input-search"
                type="input"
                name="search_value"
                placeholder={t('Search saved collection')}
                value={searchKeyword}
                onChange={handleChange}
                className={classes.inputBox}
              />
            </Form.Group>
          </Form>
        </div>
        <div className={[classes.formSet, classes.formNear].join(' ')}>
          <Form.Label className={classes.formLabel}>{t('Near')}</Form.Label>
          <AutoComplete
            elementId={autoCompleteElementId}
            data-cy="input-location"
            locationObject={locationSearch}
            saveLocationFn={setLocationSearch}
            afterSubmit={handleSubmit}
            defaultLocation={''}
            additionalClasses={classes.autocompleteStyles}
          />
        </div>
        <div className={[classes.formSet, classes.formSearchBtn].join(' ')}>
          <Button
            dataCy={'button-search'}
            type="primary"
            icon={{ style: 'fas', font: 'search' }}
            onButtonClick={handleSubmit}
            text={t('Search')}
            styles={{ width: isMobileOrTablet ? 'full' : null }}
          />
        </div>

        {showSearchCount && (
          <div>
            <span>
              <b>{t('total results found', { total: recordCount, result: recordCount > 1 ? t('Results') : t('Result') })}</b>
            </span>
            <span className="m-3">
              <Button type="link" onButtonClick={clearSearch} text={t('Clear search')} />
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionSearch;
